/**
 * Order detail page component
 */

import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useOrder, useOrderLogs, useBotStatus } from '../hooks/useOrders';
import { toDate, LogEntry } from '../types';
import '../styles/OrderDetail.css';

export function OrderDetail() {
  const { orderId } = useParams<{ orderId: string }>();
  const { order, loading: orderLoading, error: orderError } = useOrder(orderId);
  const { logs, loading: logsLoading } = useOrderLogs(orderId);
  const { botStatus } = useBotStatus();
  
  // Step detail popup state
  const [selectedStep, setSelectedStep] = useState<LogEntry | null>(null);
  
  // Accordion states - all collapsed by default for cleaner view
  const [logsOpen, setLogsOpen] = useState(false);
  const [internalNotesOpen, setInternalNotesOpen] = useState(false); // Collapsed by default

  // Check if bot is currently processing THIS order
  const isBotProcessingThisOrder = botStatus?.state === 'processing' && 
    (botStatus?.current_order_id === orderId || botStatus?.woocommerce_order_id === order?.woocommerce_order_id);

  if (orderLoading) {
    return <div className="loading">Loading order...</div>;
  }

  if (orderError || !order) {
    return (
      <div className="error-page">
        <h1>Order Not Found</h1>
        <p>{orderError || 'The requested order could not be found.'}</p>
        <Link to="/bot" className="button">Wróć do bota</Link>
      </div>
    );
  }

  return (
    <div className="order-detail">
      <div className="page-header">
        <div>
          <Link to="/bot" className="back-link">← Wróć do bota</Link>
          <h1>Order Details</h1>
        </div>
        <div className="header-right">
          <div className="status-container">
            <div className="status-item">
              <span className="status-label">Status Bota:</span>
              <span className={`status-badge large ${order.status.replace(/_/g, '-')}`}>
                {order.status === 'ordered_awaiting_payment' ? 'Awaiting Payment' :
                 order.status === 'ordered_by_woocommerce' ? 'Synced from WC' :
                 order.status === 'product_unavailable' ? 'Unavailable' :
                 order.status}
              </span>
            </div>
            {order.wc_status && (
              <div className="status-item">
                <span className="status-label">Status WooCommerce:</span>
                <span className={`status-badge large wc-status wc-${order.wc_status}${order.wc_meta?.order_complaint ? ' has-complaint' : ''}`}>
                  {order.wc_status}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Live Bot Preview - shows at the TOP when bot is processing this order */}
      {isBotProcessingThisOrder && botStatus && (
        <LiveBotPreview botStatus={botStatus} />
      )}

      <div className="detail-grid">
        {/* Order Information */}
        <div className="detail-section">
          <h2>Order Information</h2>
          <div className="detail-row">
            <span className="label">WooCommerce Order ID:</span>
            <span className="value">
              <a 
                href={`https://www.kamar-zabawki.pl/wp-admin/admin.php?page=wc-orders&action=edit&id=${order.woocommerce_order_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="order-id-link"
              >
                #{order.woocommerce_order_id}
              </a>
            </span>
          </div>
          <div className="detail-row">
            <span className="label">Supplier Order ID:</span>
            <span className="value">
              {order.supplier_order_id || '⏳'}
            </span>
          </div>

          <div className="detail-row">
            <span className="label">Created:</span>
            <span className="value">{toDate(order.created_at).toLocaleString()}</span>
          </div>
          {order.processing_started_at && (
            <div className="detail-row">
              <span className="label">Processing Started:</span>
              <span className="value">{toDate(order.processing_started_at).toLocaleString()}</span>
            </div>
          )}
          {order.processing_completed_at && (
            <div className="detail-row">
              <span className="label">Processing Completed:</span>
              <span className="value">{toDate(order.processing_completed_at).toLocaleString()}</span>
            </div>
          )}
          <div className="detail-row">
            <span className="label">Retry Count:</span>
            <span className="value">{order.retry_count}</span>
          </div>
        </div>

        {/* Customer Information */}
        <div className="detail-section">
          <h2>Customer Information</h2>
          <div className="detail-row">
            <span className="label">Name:</span>
            <span className="value">
              {order.customer.first_name} {order.customer.last_name}
            </span>
          </div>
          {order.customer.company && (
            <div className="detail-row">
              <span className="label">Company:</span>
              <span className="value">{order.customer.company}</span>
            </div>
          )}
          <div className="detail-row">
            <span className="label">Phone:</span>
            <span className="value">
              <a href={`tel:${order.customer.phone}`} className="contact-link">
                📞 {order.customer.phone}
              </a>
            </span>
          </div>
          {order.customer.email && (
            <div className="detail-row">
              <span className="label">Email:</span>
              <span className="value">
                <a href={`mailto:${order.customer.email}`} className="contact-link">
                  ✉️ {order.customer.email}
                </a>
              </span>
            </div>
          )}
        </div>

        {/* Shipping Information */}
        <div className="detail-section">
          <h2>Shipping Information</h2>
          <div className="detail-row">
            <span className="label">Delivery Method:</span>
            <span className="value">{order.shipping.delivery_method}</span>
          </div>
          {order.shipping.paczkomat_code && (
            <div className="detail-row">
              <span className="label">Paczkomat Code:</span>
              <span className="value">{order.shipping.paczkomat_code}</span>
            </div>
          )}
          <div className="detail-row">
            <span className="label">Address:</span>
            <span className="value">{order.shipping.address}</span>
          </div>
          <div className="detail-row">
            <span className="label">City:</span>
            <span className="value">
              {order.shipping.postcode} {order.shipping.city}
            </span>
          </div>
          {order.wc_meta?.shipping_method && (
            <div className="detail-row">
              <span className="label">Metoda wysyłki:</span>
              <span className="value">{order.wc_meta.shipping_method}</span>
            </div>
          )}
        </div>

        {/* Payment Information - 4th section in same grid */}
        {order.wc_meta && (
          <div className="detail-section">
            <h2>💳 Płatność</h2>
            <div className="detail-row">
              <span className="label">Metoda płatności:</span>
              <span className="value">{order.wc_meta.payment_method_title || '-'}</span>
            </div>
            <div className="detail-row">
              <span className="label">Wartość zamówienia:</span>
              <span className="value total-amount">
                {order.wc_meta.total ? `${order.wc_meta.total} ${order.wc_meta.currency || 'PLN'}` : '-'}
              </span>
            </div>
            {order.wc_meta.shipping_total && (
              <div className="detail-row">
                <span className="label">Koszt wysyłki:</span>
                <span className="value">{order.wc_meta.shipping_total} {order.wc_meta.currency || 'PLN'}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Order Complaint Alert - Always visible, full width, right after payment grid */}
      {order.wc_meta?.order_complaint && (
        <div className="detail-section full-width complaint-alert-section">
          <div className="complaint-alert">
            <div className="complaint-alert-header">
              <span className="complaint-alert-icon">⚠️</span>
              <span className="complaint-alert-title">Reklamacja / Problem</span>
            </div>
            <div className="complaint-alert-message">{order.wc_meta.order_complaint}</div>
          </div>
        </div>
      )}

      {/* Internal Notes - Collapsible Accordion, collapsed by default */}
      {(order.wc_meta?.customer_note || order.wc_meta?.order_comments) && (
        <div className="detail-section full-width accordion-section internal-notes-section">
          <div 
            className="accordion-header"
            onClick={() => setInternalNotesOpen(!internalNotesOpen)}
          >
            <h2>📝 Komentarze wewnętrzne</h2>
            <span className={`accordion-arrow ${internalNotesOpen ? 'open' : ''}`}>▼</span>
          </div>
          {internalNotesOpen && (
            <div className="accordion-content">
              {order.wc_meta.customer_note && (
                <div className="detail-row note-row">
                  <span className="label">Uwagi klienta:</span>
                  <span className="value note-value">{order.wc_meta.customer_note}</span>
                </div>
              )}
              {order.wc_meta.order_comments && (
                <div className="detail-row note-row">
                  <span className="label">Komentarze zamówienia:</span>
                  <span className="value note-value">{order.wc_meta.order_comments}</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Order Items */}
      <div className="detail-section full-width">
        <h2>Order Items</h2>
        <table className="items-table">
          <thead>
            <tr>
              <th>SKU</th>
              <th>Product Name</th>
              <th>Quantity</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {order.items.map((item, index) => {
              const isSuccess = order.successful_items.includes(item.sku);
              const isFailed = order.failed_items.includes(item.sku);
              const status = isSuccess ? 'success' : isFailed ? 'failed' : 'pending';

              return (
                <tr key={index}>
                  <td>{item.sku}</td>
                  <td>{item.name}</td>
                  <td>{item.quantity}</td>
                  <td>
                    <span className={`item-status ${status}`}>
                      {status}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Step Detail Popup Modal */}
      {selectedStep && (
        <div 
          className="step-modal-overlay" 
          onClick={() => setSelectedStep(null)}
        >
          <div className="step-modal-content" onClick={(e) => e.stopPropagation()}>
            <button 
              className="step-modal-close" 
              onClick={() => setSelectedStep(null)}
            >
              ✕
            </button>
            
            <div className="step-modal-header">
              <span className={`step-modal-level ${selectedStep.level}`}>
                {selectedStep.level === 'error' ? '❌' : selectedStep.level === 'warn' ? '⚠️' : 'ℹ️'}
              </span>
              <h3>{selectedStep.message || 'Krok przetwarzania'}</h3>
            </div>
            
            <div className="step-modal-body">
              <div className="step-modal-time">
                🕐 {toDate(selectedStep.timestamp).toLocaleString()}
              </div>
              
              {selectedStep.details && Object.keys(selectedStep.details).length > 0 && (
                <div className="step-modal-details">
                  <h4>Szczegóły:</h4>
                  <dl className="step-details-list">
                    {selectedStep.details.step_name && (
                      <>
                        <dt>Nazwa kroku:</dt>
                        <dd>{selectedStep.details.step_name}</dd>
                      </>
                    )}
                    {selectedStep.details.step_number && (
                      <>
                        <dt>Numer kroku:</dt>
                        <dd>{selectedStep.details.step_number}</dd>
                      </>
                    )}
                    {selectedStep.details.productSku && (
                      <>
                        <dt>SKU produktu:</dt>
                        <dd>{selectedStep.details.productSku}</dd>
                      </>
                    )}
                    {selectedStep.details.productName && (
                      <>
                        <dt>Nazwa produktu:</dt>
                        <dd>{selectedStep.details.productName}</dd>
                      </>
                    )}
                    {selectedStep.details.action && (
                      <>
                        <dt>Akcja:</dt>
                        <dd>{selectedStep.details.action}</dd>
                      </>
                    )}
                    {selectedStep.details.supplierOrderId && (
                      <>
                        <dt>Nr zamówienia dostawcy:</dt>
                        <dd className="supplier-order-id">{selectedStep.details.supplierOrderId}</dd>
                      </>
                    )}
                    {selectedStep.details.totalAmount && (
                      <>
                        <dt>Kwota:</dt>
                        <dd>{selectedStep.details.totalAmount} PLN</dd>
                      </>
                    )}
                    {selectedStep.details.pageUrl && (
                      <>
                        <dt>URL strony:</dt>
                        <dd className="page-url">{selectedStep.details.pageUrl}</dd>
                      </>
                    )}
                    {selectedStep.details.error && (
                      <>
                        <dt>Błąd:</dt>
                        <dd className="error-text">{selectedStep.details.error}</dd>
                      </>
                    )}
                    {selectedStep.details.message && selectedStep.details.message !== selectedStep.message && (
                      <>
                        <dt>Wiadomość:</dt>
                        <dd>{selectedStep.details.message}</dd>
                      </>
                    )}
                  </dl>
                </div>
              )}
              
            </div>
          </div>
        </div>
      )}

      {/* Execution Logs - Collapsible */}
      <div className="detail-section full-width logs-section accordion-section">
        <div 
          className="accordion-header"
          onClick={() => setLogsOpen(!logsOpen)}
        >
          <h2>📋 Historia przetwarzania ({logs.length})</h2>
          <span className={`accordion-arrow ${logsOpen ? 'open' : ''}`}>▼</span>
        </div>
        
        {logsOpen && (
          <div className="accordion-content">
            {/* Error Message at the top of logs */}
            {order.error_message && (
              <div className="error-alert">
                <div className="error-alert-header">
                  <span className="error-alert-icon">❌</span>
                  <span className="error-alert-title">Błąd przetwarzania</span>
                </div>
                <div className="error-alert-message">{order.error_message}</div>
              </div>
            )}
            
            {logsLoading ? (
              <div className="loading">Ładowanie logów...</div>
            ) : logs.length > 0 ? (
              <ProcessingRunsView 
                logs={logs} 
                onStepClick={setSelectedStep}
              />
            ) : (
              <div className="empty">Brak logów dla tego zamówienia</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Live Bot Preview with working timer
 */
import { BotStatus } from '../types';

function LiveBotPreview({ botStatus }: { botStatus: BotStatus }) {
  const [elapsedTime, setElapsedTime] = useState('0:00');

  // Live timer that updates every second
  useEffect(() => {
    if (!botStatus.started_at) {
      setElapsedTime('0:00');
      return;
    }

    const updateTimer = () => {
      const startTime = toDate(botStatus.started_at!);
      const elapsed = Math.floor((Date.now() - startTime.getTime()) / 1000);
      const minutes = Math.floor(elapsed / 60);
      const seconds = elapsed % 60;
      setElapsedTime(`${minutes}:${seconds.toString().padStart(2, '0')}`);
    };

    updateTimer(); // Initial update
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [botStatus.started_at]);

  const progressPercent = botStatus.total_steps 
    ? Math.round(((botStatus.step_number || 1) / botStatus.total_steps) * 100)
    : 0;

  return (
    <div className="detail-section full-width bot-live-preview">
      <h2>🤖 Bot pracuje na tym zamówieniu</h2>
      <div className="bot-progress-container">
        {/* Timer - large and prominent */}
        <div className="bot-timer-large">
          <span className="bot-timer-label">⏱️ Czas pracy:</span>
          <span className="bot-timer-value">{elapsedTime}</span>
        </div>

        <div className="bot-progress-header">
          <div className="bot-step-info">
            <span className="bot-step-number">
              Krok {botStatus.step_number || 1} z {botStatus.total_steps || 12}
            </span>
            <span className={`bot-step-status ${botStatus.step_status || 'running'}`}>
              {botStatus.step_status === 'running' && '⏳ W trakcie'}
              {botStatus.step_status === 'success' && '✅ Sukces'}
              {botStatus.step_status === 'error' && '❌ Błąd'}
            </span>
          </div>
          <div className="bot-current-step">
            {botStatus.current_step || 'Inicjalizacja...'}
          </div>
          {botStatus.step_details && (
            <div className="bot-step-details">{botStatus.step_details}</div>
          )}
        </div>
        
        {/* Progress bar with percentage */}
        <div className="bot-progress-bar-section">
          <div className="bot-progress-bar">
            <div 
              className="bot-progress-fill" 
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <span className="bot-progress-percent">{progressPercent}%</span>
        </div>

        {/* Products progress */}
        {botStatus.items_total && botStatus.items_total > 0 && (
          <div className="bot-items-section">
            <span className="bot-items-label">📦 Produkty:</span>
            <div className="bot-items-progress">
              <span className="bot-items-count">
                {botStatus.items_processed || 0} / {botStatus.items_total}
              </span>
              <div className="bot-items-bar">
                <div 
                  className="bot-items-fill" 
                  style={{ width: `${((botStatus.items_processed || 0) / botStatus.items_total) * 100}%` }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Current product if available */}
        {botStatus.current_product && (
          <div className="bot-current-product">
            <span className="bot-product-label">🛒 Aktualny produkt:</span>
            <span className="bot-product-name">{botStatus.current_product}</span>
          </div>
        )}

        {/* Last action */}
        {botStatus.last_action && (
          <div className="bot-last-action">
            <span className="bot-action-label">🔄 Ostatnia akcja:</span>
            <span className="bot-action-value">{botStatus.last_action}</span>
          </div>
        )}

        {/* Error message */}
        {botStatus.error_message && (
          <div className="bot-error-display">
            <span className="bot-error-label">❌ Błąd:</span>
            <span className="bot-error-value">{botStatus.error_message}</span>
          </div>
        )}

        {/* Live Console Logs */}
        {botStatus.console_logs && botStatus.console_logs.length > 0 && (
          <div className="bot-console-logs">
            <span className="bot-console-label">📋 Console logs ({botStatus.console_logs.length}):</span>
            <div className="bot-console-container">
              {[...botStatus.console_logs].reverse().slice(0, 20).map((log, index) => {
                const timestamp = toDate(log.timestamp);
                const levelClass = `console-${log.level}`;
                const levelIcon = {
                  info: 'ℹ️',
                  warn: '⚠️',
                  error: '❌',
                  debug: '🔍',
                  step: '🔄',
                }[log.level] || 'ℹ️';
                
                return (
                  <div key={index} className={`bot-console-entry ${levelClass}`}>
                    <span className="console-time">{timestamp.toLocaleTimeString()}</span>
                    <span className="console-icon">{levelIcon}</span>
                    <span className="console-message">{log.message}</span>
                    {log.details && (
                      <span className="console-details" title={log.details}>...</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Start time */}
        {botStatus.started_at && (
          <div className="bot-timing">
            Rozpoczęto: {toDate(botStatus.started_at).toLocaleTimeString()}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Interface for a processing run (grouped logs from one execution)
 */
interface ProcessingRun {
  id: string;
  startTime: Date;
  endTime: Date;
  logs: LogEntry[];
  status: 'success' | 'error' | 'partial' | 'running';
  stepCount: number;
  duration: number; // in seconds
}

/**
 * Group logs into processing runs based on time gaps
 * A new run starts when there's a gap > 5 minutes between logs
 */
function groupLogsIntoRuns(logs: LogEntry[]): ProcessingRun[] {
  if (logs.length === 0) return [];
  
  // Sort logs by timestamp (oldest first)
  const sortedLogs = [...logs].sort((a, b) => 
    toDate(a.timestamp).getTime() - toDate(b.timestamp).getTime()
  );
  
  const runs: ProcessingRun[] = [];
  let currentRun: LogEntry[] = [sortedLogs[0]];
  
  for (let i = 1; i < sortedLogs.length; i++) {
    const prevTime = toDate(sortedLogs[i - 1].timestamp).getTime();
    const currTime = toDate(sortedLogs[i].timestamp).getTime();
    const gapMinutes = (currTime - prevTime) / (1000 * 60);
    
    // If gap > 5 minutes, start a new run
    if (gapMinutes > 5) {
      runs.push(createRunFromLogs(currentRun, runs.length + 1));
      currentRun = [sortedLogs[i]];
    } else {
      currentRun.push(sortedLogs[i]);
    }
  }
  
  // Don't forget the last run
  if (currentRun.length > 0) {
    runs.push(createRunFromLogs(currentRun, runs.length + 1));
  }
  
  // Return in reverse order (newest first)
  return runs.reverse();
}

function createRunFromLogs(logs: LogEntry[], runNumber: number): ProcessingRun {
  const startTime = toDate(logs[0].timestamp);
  const endTime = toDate(logs[logs.length - 1].timestamp);
  
  // Determine status based on logs
  const hasError = logs.some(l => l.level === 'error');
  const hasSuccess = logs.some(l => 
    l.message?.includes('✅') || 
    l.details?.supplierOrderId || 
    l.message?.toLowerCase().includes('sukces')
  );
  
  let status: ProcessingRun['status'] = 'running';
  if (hasError && hasSuccess) status = 'partial';
  else if (hasError) status = 'error';
  else if (hasSuccess) status = 'success';
  
  // Count unique steps
  const steps = new Set(logs.map(l => l.details?.step_number).filter(Boolean));
  
  return {
    id: `run-${runNumber}`,
    startTime,
    endTime,
    logs,
    status,
    stepCount: steps.size || logs.length,
    duration: Math.round((endTime.getTime() - startTime.getTime()) / 1000)
  };
}

/**
 * Component to display grouped processing runs with step cards
 */
function ProcessingRunsView({ logs, onStepClick }: { logs: LogEntry[]; onStepClick?: (log: LogEntry) => void }) {
  const [expandedRun, setExpandedRun] = useState<string | null>(null);
  
  // Filter meaningful logs first
  const meaningfulLogs = logs.filter((log) => {
    const hasRealMessage = log.message && 
      log.message.trim() && 
      log.message.trim() !== 'Log' &&
      log.message.trim() !== 'Wystąpił błąd' &&
      log.message.trim() !== 'Ostrzeżenie' &&
      log.message.length > 3;
    
    let hasMeaningfulDetails = false;
    if (log.details && typeof log.details === 'object') {
      const d = log.details;
      hasMeaningfulDetails = !!(
        (d.step_name && d.step_name.length > 0) || 
        (d.error && d.error.length > 0) || 
        (d.message && d.message.length > 0) || 
        (d.action && d.action.length > 0) || 
        (d.url && d.url.length > 0) || 
        (d.pageUrl && d.pageUrl.length > 0) ||
        d.supplierOrderId ||
        d.productSku ||
        d.productName ||
        d.totalAmount ||
        d.promoApplied !== undefined
      );
    }
    
    return hasRealMessage || hasMeaningfulDetails;
  });
  
  const runs = groupLogsIntoRuns(meaningfulLogs);
  
  // Auto-expand first run on mount
  useState(() => {
    if (runs.length > 0 && !expandedRun) {
      setExpandedRun(runs[0].id);
    }
  });
  
  const getRunStatusIcon = (status: ProcessingRun['status']) => {
    switch (status) {
      case 'success': return '✅';
      case 'error': return '❌';
      case 'partial': return '⚠️';
      default: return '⏳';
    }
  };
  
  const getRunStatusLabel = (status: ProcessingRun['status']) => {
    switch (status) {
      case 'success': return 'Sukces';
      case 'error': return 'Błąd';
      case 'partial': return 'Częściowo';
      default: return 'W trakcie';
    }
  };
  
  const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };
  
  const getStepIcon = (log: LogEntry) => {
    const msg = log.message || '';
    const d = log.details || {};
    
    if (msg.includes('✅') || d.supplierOrderId) return '✅';
    if (msg.includes('❌') || log.level === 'error') return '❌';
    if (msg.includes('🔐') || msg.toLowerCase().includes('login')) return '🔐';
    if (msg.includes('🛒') || msg.toLowerCase().includes('koszyk')) return '🛒';
    if (msg.includes('📦') || d.productSku) return '📦';
    if (msg.includes('💳') || msg.toLowerCase().includes('płat')) return '💳';
    if (d.step_number) return `${d.step_number}`;
    if (log.level === 'warn') return '⚠️';
    return '•';
  };
  
  const getStepTitle = (log: LogEntry) => {
    const d = log.details || {};
    if (d.step_name) return d.step_name;
    if (log.message) {
      // Extract meaningful part of message
      let msg = log.message;
      if (msg.startsWith('[Krok')) {
        msg = msg.replace(/^\[Krok \d+\]\s*/, '');
      }
      return msg.length > 50 ? msg.substring(0, 50) + '...' : msg;
    }
    return 'Krok przetwarzania';
  };
  
  return (
    <div className="processing-runs">
      {runs.map((run, runIndex) => (
        <div key={run.id} className={`processing-run ${run.status}`}>
          {/* Run header */}
          <div 
            className="processing-run-header"
            onClick={() => setExpandedRun(expandedRun === run.id ? null : run.id)}
          >
            <div className="run-header-left">
              <span className="run-number">Uruchomienie #{runs.length - runIndex}</span>
              <span className={`run-status-badge ${run.status}`}>
                {getRunStatusIcon(run.status)} {getRunStatusLabel(run.status)}
              </span>
            </div>
            <div className="run-header-right">
              <span className="run-time">{run.startTime.toLocaleString()}</span>
              <span className="run-duration">⏱️ {formatDuration(run.duration)}</span>
              <span className="run-steps-count">{run.logs.length} kroków</span>
              <span className={`run-expand-arrow ${expandedRun === run.id ? 'open' : ''}`}>▼</span>
            </div>
          </div>
          
          {/* Run content - steps grid */}
          {expandedRun === run.id && (
            <div className="processing-run-content">
              <div className="steps-grid">
                {run.logs.map((log, stepIndex) => (
                  <div 
                    key={log.id} 
                    className={`step-card ${log.level} clickable`}
                    onClick={() => onStepClick?.(log)}
                  >
                    <div className="step-card-header">
                      <span className="step-number-badge">{stepIndex + 1}</span>
                      <span className="step-icon">{getStepIcon(log)}</span>
                    </div>
                    <div className="step-card-title">{getStepTitle(log)}</div>
                    <div className="step-card-time">
                      {toDate(log.timestamp).toLocaleTimeString()}
                    </div>
                    {log.details?.productSku && (
                      <div className="step-card-sku">SKU: {log.details.productSku}</div>
                    )}
                    {log.level === 'error' && log.details?.error && (
                      <div className="step-card-error">{log.details.error.substring(0, 60)}...</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
